import { ShiftManagementBulkDeleteRestDaysDocument } from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { trackDeleteRestDaysEvent } from 'modules/shift_management/lib/trackDeleteEvent'
import type { RestDay, ShiftsRow } from 'modules/shift_management/lib/types'

import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

const useDestroyRestDay = (restDay: RestDay) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats()
  const restDaysIds = [restDay.id]

  const [destroyRestDays, { loading }] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
  })

  const onDestroy = async (closePortal: ClosePortalFn) => {
    const result = await destroyRestDays({
      variables: { ids: restDaysIds },
      update: (cache, { data }) => {
        if (data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays) {
          data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays.forEach(
            (restDay) => {
              cache.evict({
                id: cache.identify(restDay),
              })
            }
          )
        }
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      dispatchShiftsRows({ type: 'DESTROY_REST_DAY', restDay })
      trackDeleteRestDaysEvent([restDay], 1, shiftsConflicts, 'contextual_button')
      await refreshStats(shiftsRows)
      closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.delete_rest_day_success'),
      })
    }
  }

  return {
    onDestroy,
    submitting: loading,
  }
}

const useDestroyRestDays = (restDays: RestDay[], employeeCount: number) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()
  const [{ shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats()

  const [destroyRestDays, { loading }] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
  })

  const onDestroy = async (closePortal: ClosePortalFn) => {
    const restDaysIds = restDays.map((restDay) => restDay.id)
    const result = await destroyRestDays({
      variables: { ids: restDaysIds },
      update: (cache, { data }) => {
        data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays?.forEach(
          (restDay) => {
            cache.evict({
              id: cache.identify(restDay),
            })
          }
        )
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      trackDeleteRestDaysEvent(restDays, employeeCount, shiftsConflicts, 'top_bar_button')

      // Get shiftsRows where there are affected shifts to update stats
      const restDayKeys = new Set(restDays.map((restDay) => String(restDay.employeeId)))
      const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => restDayKeys.has(row.key))
      await refreshStats(previousShiftsRows)

      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays })
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.delete_rest_day_success'),
      })
    }
  }

  return {
    onDestroy,
    submitting: loading,
  }
}

export { useDestroyRestDay, useDestroyRestDays }
